/**
 * チャートドラッグ編集 (AC-006) の純関数ユーティリティ。
 * design 参照: design/demand/forecast-today.html L2093-2266 (チャートドラッグ編集)。
 *
 * DOM 依存部分 (Y軸 tick の pixel 読み取り) と数値計算部分を分離し、数値計算のみを
 * ユニットテスト対象にする (DOM 依存関数は happy-dom の getBoundingClientRect が
 * 常に 0 を返すため意味のあるテストができない。ForecastChart 側で DOM 読み取り→
 * 本ファイルの純関数、の順で組み立てる)。
 */
import {
  DAY_BOUNDARY_PRE_EXTENSION_KOMAS,
  applyProportional,
  normalizeFloat,
  updateKomaSlot,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** Y軸目盛りの pixel↔値マッピングを構築するための入力点 (DOM から読み取った tick 座標)。 */
export type YAxisTickPoint = {
  /** tick の中心 y 座標 (client 座標系、pixel) */
  y: number
  /** tick が表す値 */
  value: number
}

/**
 * Y軸目盛りの pixel↔値の線形マッピングを構築する
 * (design forecast-today.html L2107-2131 `_dftReadYScale` 準拠)。
 *
 * 2点未満、または全点が同一 pixel/値 の場合は変換不能のため undefined を返す。
 */
export function buildYAxisScale(
  points: YAxisTickPoint[],
): ((clientY: number) => number) | undefined {
  if (points.length < 2) return undefined
  const sorted = [...points].sort((a, b) => a.y - b.y)
  const top = sorted[0]
  const bottom = sorted[sorted.length - 1]
  if (top === undefined || bottom === undefined) return undefined
  if (top.y === bottom.y || top.value === bottom.value) return undefined
  const slope = (bottom.value - top.value) / (bottom.y - top.y)
  return (clientY: number): number => top.value + (clientY - top.y) * slope
}

/**
 * Recharts が描画したチャート container から「左 Y 軸の tick ラベルを含む要素」を特定する。
 *
 * Recharts 3.x は tick text を軸 g (`.recharts-yAxis`) の**外**の zIndex レイヤ
 * (`.recharts-zIndex-layer_*` 直下の `.recharts-yAxis-tick-labels`) に描画するため、
 * ラベルグループを直接探す。2.x 系 (design プロトタイプの前提) は軸 g 内に text が
 * 同居するため `.recharts-yAxis` に fallback する。いずれも複数ヒット時
 * (左 = 需要 kWh 軸 / 右 = 気温 ℃ 軸) は bounding left 最小の要素を左軸とする
 * (design forecast-today.html L2112-2113 と同じ判定)。
 */
export function findLeftYAxisLabelGroup(
  container: Element,
): Element | undefined {
  let groups = Array.from(
    container.querySelectorAll('.recharts-yAxis-tick-labels'),
  )
  if (groups.length === 0) {
    groups = Array.from(container.querySelectorAll('.recharts-yAxis'))
  }
  if (groups.length === 0) return undefined
  groups.sort(
    (a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left,
  )
  return groups[0]
}

/**
 * Recharts が描画した Y 軸要素から tick の (pixel, 値) ペアを抽出する。
 *
 * Recharts 3.x は tick line (`.recharts-cartesian-axis-tick`、tick-lines グループ) と
 * tick text (`.recharts-cartesian-axis-tick-value`、tick-labels グループ) を**別の g に
 * 分離して描画する**ため、text を直接拾う。design プロトタイプが前提にした 2.x 系の
 * 「tick g 内に line と text が同居」構造には fallback セレクタで対応する
 * (2.x 前提の tick 内 text 探索だけだと 3.x で 1 件も取れず、ドラッグ編集が
 * 全く開始しないバグになる — chartDrag.test.ts の regression fixture 参照)。
 *
 * 値のパースは桁区切りカンマ・単位文字 (kWh 等) を除去して行い、数値にならない
 * text (軸単位ラベル等) はスキップする。
 */
export function extractYAxisTickPoints(axis: Element): YAxisTickPoint[] {
  let texts = Array.from(
    axis.querySelectorAll<SVGTextElement>(
      '.recharts-cartesian-axis-tick-value',
    ),
  )
  if (texts.length === 0) {
    texts = Array.from(
      axis.querySelectorAll<SVGTextElement>(
        '.recharts-cartesian-axis-tick text',
      ),
    )
  }
  const points: YAxisTickPoint[] = []
  for (const text of texts) {
    const value = parseFloat((text.textContent ?? '').replace(/[^0-9.-]/g, ''))
    if (!Number.isFinite(value)) continue
    const rect = text.getBoundingClientRect()
    points.push({ y: rect.top + rect.height / 2, value })
  }
  return points
}

/**
 * Recharts の `activeTooltipIndex` から、気象帯で強調する「時」 (0〜23) を解決する。
 *
 * コマ 1・2 → 0 時 … コマ 47・48 → 23 時。 8080 (`js/charts.js:948`) の
 * `Math.floor((curKoma - 1) / 2)` と同じ写像で、 1 時間 = 2 コマ に対応する。
 *
 * **GC 済みかどうかは見ない。** [[resolveDragKoma]] は編集可否を解決するので GC 済みを
 * 落とすが、 こちらは見た目の強調なので「編集できないコマにホバーしても気象は光る」 が
 * 正しい。 前端の延長部 (前日 23:00/23:30) と範囲外は対象の時が無いので undefined。
 */
export function resolveHoverWeatherHour(
  activeTooltipIndex: number | string | null | undefined,
): number | undefined {
  // 空文字は `Number('')` が 0 になり index 0 と解釈されてしまうので弾く
  // (ホバーが外れた時に空文字が来ても 0 時を光らせない)。
  const slotIdx =
    typeof activeTooltipIndex === 'number'
      ? activeTooltipIndex
      : typeof activeTooltipIndex === 'string' &&
          activeTooltipIndex.trim() !== ''
        ? Number(activeTooltipIndex)
        : NaN
  if (
    !Number.isInteger(slotIdx) ||
    slotIdx < DAY_BOUNDARY_PRE_EXTENSION_KOMAS ||
    slotIdx >= DAY_BOUNDARY_PRE_EXTENSION_KOMAS + 48
  ) {
    return undefined
  }
  return Math.floor((slotIdx - DAY_BOUNDARY_PRE_EXTENSION_KOMAS) / 2)
}

/**
 * 編集対象コマ番号 (1-indexed) をチャートの描画点の添字に写す。 [[resolveDragKoma]] の逆写像で、
 * ドラッグ開始時に元値を引く点を解決する (前端の延長部 2 点の後ろからコマ 1 が始まる)。
 */
export function komaToChartIndex(koma: number): number {
  return koma - 1 + DAY_BOUNDARY_PRE_EXTENSION_KOMAS
}

/**
 * Recharts の `activeTooltipIndex` (mousedown 時のチャート状態) から編集対象コマ番号
 * (1-indexed) を解決する。前端の延長部 (前日 23:00/23:30)・後端の延長部
 * または GCロック済みコマは undefined (= ドラッグ開始不可) を返す。
 */
export function resolveDragKoma(
  activeTooltipIndex: number | string | null | undefined,
  gcKoma: number,
): number | undefined {
  const slotIdx =
    typeof activeTooltipIndex === 'number'
      ? activeTooltipIndex
      : activeTooltipIndex !== null && activeTooltipIndex !== undefined
        ? Number(activeTooltipIndex)
        : NaN
  if (
    !Number.isInteger(slotIdx) ||
    slotIdx < DAY_BOUNDARY_PRE_EXTENSION_KOMAS ||
    slotIdx >= DAY_BOUNDARY_PRE_EXTENSION_KOMAS + 48
  ) {
    return undefined
  }
  const koma = slotIdx - DAY_BOUNDARY_PRE_EXTENSION_KOMAS + 1
  if (gcKoma > 0 && koma <= gcKoma) return undefined
  return koma
}

/**
 * ドラッグ開始位置 (clickedValue) が需要想定値 (forecastValue) より他の表示系列
 * (otherValues、過去実績・確定履歴等) の値に近い場合、誤掴み防止のためドラッグを
 * 拒否する (design forecast-today.html L2157-2168 準拠)。
 *
 * forecastValue が未定義 (= その コマの需要想定値が空セル) の場合も拒否する。
 */
export function isForecastGrabTarget(
  clickedValue: number,
  forecastValue: number | undefined,
  otherValues: Array<number | undefined>,
): boolean {
  if (forecastValue === undefined) return false
  const forecastDist = Math.abs(clickedValue - forecastValue)
  return otherValues.every((other) => {
    if (other === undefined) return true
    return Math.abs(clickedValue - other) >= forecastDist
  })
}

/** `computeDragTick` の結果。 */
export type DragTickResult = {
  koma: number
  /** 保存単位 (送電端 kWh/30min) に変換済みの値 */
  value: number
  /** 更新後の 48コマ配列 (dragMode=1 の場合は比例配分適用済み) */
  slots: KomaSlots
}

/**
 * ドラッグ中の 1 tick 分の状態遷移 (mousemove) を計算する
 * (design forecast-today.html L2183-2199 準拠)。
 *
 * Y座標→値変換 (scale) は呼び出し側の責務とし、本関数は
 * 「値のクランプ・単位変換・一括同一値/比例配分/単一更新の分岐」のみを担当する。
 *
 * newDisplayValue が 0 未満になる場合は更新を拒否し undefined を返す
 * (design のガード `newVal == null || newVal < 0` 準拠)。値は丸めず小数のまま
 * 使う (AC-DFC-EDIT-003)。
 */
export function computeDragTick(params: {
  baselineSlots: KomaSlots
  forecastSlots: KomaSlots
  koma: number
  newDisplayValue: number
  dragMode: 0 | 1
  gcKoma: number
}): DragTickResult | undefined {
  // 表示値と保存値はどちらも送電端 kWh/30min (AC-DFC-011) なので単位変換はない。
  // Y 座標→値変換の演算誤差だけ吸収する (丸めではない。AC-DFC-EDIT-003)。
  const value = normalizeFloat(params.newDisplayValue)
  if (value < 0) return undefined
  const slots =
    params.dragMode === 1
      ? applyProportional(
          params.baselineSlots,
          params.forecastSlots,
          params.koma,
          value,
          params.gcKoma,
        )
      : updateKomaSlot(params.forecastSlots, params.koma, value)
  return { koma: params.koma, value, slots }
}
